import { useState, useEffect, type CSSProperties, type MouseEvent } from "react";

const STORAGE_KEY = "taidotech_cookie_consent";
const EXPIRY_DAYS = 365;

type Choice = "accepted" | "declined" | "dismissed";

interface StoredConsent {
  choice: Choice;
  timestamp: number;
}

declare global {
  interface Window {
    TAIDOTECH_ANALYTICS_ID?: string;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function hasExpired(stored: StoredConsent | null): boolean {
  if (!stored?.timestamp) return true;
  const age = Date.now() - stored.timestamp;
  return age > EXPIRY_DAYS * 24 * 60 * 60 * 1000;
}

function loadAnalytics() {
  if (typeof window === "undefined" || !window.TAIDOTECH_ANALYTICS_ID) return;
  const id = window.TAIDOTECH_ANALYTICS_ID;
  if (document.querySelector(`script[data-ga-id="${id}"]`)) return;
  const s = document.createElement("script");
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  s.async = true;
  s.dataset.gaId = id;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  const gtag = (...args: unknown[]) => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", id);
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let idleHandle: ReturnType<typeof requestIdleCallback> | undefined;

    // Defer the banner mount until after the page is idle and past Lighthouse's
    // Largest Contentful Paint measurement window. If we mount earlier, the
    // banner's text becomes the LCP element and tanks the performance score.
    // Mobile takes longer than desktop to settle, so we use a generous delay
    // (~6s) to make sure the banner mounts past Lighthouse's mobile LCP window.
    const showLater = () => {
      timeout = setTimeout(() => setVisible(true), 6000);
    };

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const stored = raw ? (JSON.parse(raw) as StoredConsent) : null;
      if (!stored || hasExpired(stored)) {
        if (typeof window.requestIdleCallback === "function") {
          idleHandle = window.requestIdleCallback(showLater, { timeout: 4000 });
        } else {
          showLater();
        }
      } else if (stored.choice === "accepted") {
        loadAnalytics();
      }
    } catch {
      showLater();
    }
    return () => {
      if (timeout) clearTimeout(timeout);
      if (idleHandle && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
    };
  }, []);

  function dismiss(choice: Choice) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, timestamp: Date.now() } satisfies StoredConsent),
      );
    } catch {
      /* storage may be unavailable; banner still hides for the session */
    }
    if (choice === "accepted") loadAnalytics();
    setLeaving(true);
    setTimeout(() => setVisible(false), 300);
  }

  if (!visible) return null;

  const wrapStyle: CSSProperties = {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    background: "#fff",
    borderTop: "3px solid #D94F5C",
    boxShadow: "0 -4px 24px rgba(27,42,74,.12)",
    fontFamily: "var(--font-body)",
    transform: leaving ? "translateY(100%)" : "translateY(0)",
    transition: "transform .3s ease",
  };

  const innerStyle: CSSProperties = {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "20px 52px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 40,
    flexWrap: "wrap",
  };

  const declineStyle: CSSProperties = {
    background: "transparent",
    border: "1px solid rgba(27,42,74,.25)",
    color: "#1B2A4A",
    fontSize: 13.5,
    fontWeight: 500,
    padding: "9px 22px",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "border-color .18s",
  };

  const acceptStyle: CSSProperties = {
    background: "#9C2F3A",
    border: "none",
    color: "#fff",
    fontSize: 13.5,
    fontWeight: 500,
    padding: "9px 22px",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background .18s",
  };

  const closeStyle: CSSProperties = {
    background: "none",
    border: "none",
    color: "rgba(27,42,74,.35)",
    fontSize: 18,
    cursor: "pointer",
    padding: "4px 8px",
    lineHeight: 1,
    fontFamily: "inherit",
    transition: "color .18s",
  };

  const setBorder = (e: MouseEvent<HTMLButtonElement>, color: string) => {
    e.currentTarget.style.borderColor = color;
  };
  const setBg = (e: MouseEvent<HTMLButtonElement>, color: string) => {
    e.currentTarget.style.background = color;
  };
  const setColor = (e: MouseEvent<HTMLButtonElement>, color: string) => {
    e.currentTarget.style.color = color;
  };

  return (
    <div role="dialog" aria-label="Cookie consent" aria-live="polite" style={wrapStyle}>
      <div style={innerStyle}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <p style={{ fontSize: 14, color: "#1B2A4A", lineHeight: 1.65, margin: 0 }}>
            We use analytics cookies to understand how visitors use our site and improve our content.
            We don't use advertising or tracking cookies.{" "}
            <a
              href={`${import.meta.env.BASE_URL}privacy`}
              style={{ color: "#B83E4A", textDecoration: "underline", textUnderlineOffset: 2 }}
            >
              Privacy Policy
            </a>
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => dismiss("declined")}
            style={declineStyle}
            onMouseEnter={(e) => setBorder(e, "#1B2A4A")}
            onMouseLeave={(e) => setBorder(e, "rgba(27,42,74,.25)")}
          >
            Decline optional cookies
          </button>
          <button
            type="button"
            onClick={() => dismiss("accepted")}
            style={acceptStyle}
            onMouseEnter={(e) => setBg(e, "#7E2530")}
            onMouseLeave={(e) => setBg(e, "#9C2F3A")}
          >
            Accept cookies
          </button>
          <button
            type="button"
            onClick={() => dismiss("dismissed")}
            aria-label="Close cookie notice"
            title="Close"
            style={closeStyle}
            onMouseEnter={(e) => setColor(e, "#1B2A4A")}
            onMouseLeave={(e) => setColor(e, "rgba(27,42,74,.35)")}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
